import { call, put, takeLatest, select, takeEvery } from 'typed-redux-saga';
import { getArticlePageData, articlePageFetchingStatus, getCategoryPageData, categoryPageFetchingStatus, getHomePageData, homePageFetchingStatus, } from '../selectors';
import { sendArticlePageEvent, sendCategoryPageEvent, sendHomePageEvent, } from './analytics';
import * as PageActions from '../actions/page';
import { fetchArticle, searchArticles } from '../api/article';
import { fetchCategory, fetchHome } from '../api/category';
import { getLanguageId } from '../utils/globals';
import { reportError } from '../bugTracking';
const getErrorStatus = (errors) => {
    if (checkErrorsForUnauth(errors)) {
        return 'NotAllowedError';
    }
    if (checkErrorsForNotFound(errors)) {
        return 'NotFound';
    }
    return 'HasError';
};
const checkErrorsForUnauth = (errors) => errors.some((err) => { var _a; return ((_a = err === null || err === void 0 ? void 0 : err.extensions) === null || _a === void 0 ? void 0 : _a.code) === 'Unauthorized'; });
const checkErrorsForNotFound = (errors) => errors.some((err) => { var _a; return ((_a = err === null || err === void 0 ? void 0 : err.extensions) === null || _a === void 0 ? void 0 : _a.code) === 'NotFound'; });
export function* requestArticlePageData(articleId, path) {
    var _a;
    try {
        if (!articleId) {
            throw new Error('Invalid article id');
        }
        // Check to see if we already have the data.
        const existingData = yield* select(getArticlePageData, articleId);
        const fetchingStatus = yield* select(articlePageFetchingStatus, articleId);
        // Don't try to reload if server has given these error types.
        if (fetchingStatus === 'LoginRequired') {
            return;
        }
        if (existingData) {
            // make sure we call article page view.
            yield* call(sendArticlePageEvent, articleId);
            return;
        }
        const languageId = yield* call(getLanguageId);
        const data = yield* call(fetchArticle, articleId, languageId);
        if (!data.article) {
            throw new Error('NotFound');
        }
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'article',
            articleId,
            path,
            data,
        }));
    }
    catch (error) {
        if (!error.response) {
            yield* call(reportError, error);
        }
        const errors = ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'article',
            articleId,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* requestCategoryPageData(categoryId, path) {
    var _a;
    try {
        if (!categoryId) {
            throw new Error('Invalid category id');
        }
        // Check to see if we already have the data.
        const existingData = yield* select(getCategoryPageData, categoryId);
        const fetchingStatus = yield* select(categoryPageFetchingStatus, categoryId);
        // Don't try to reload if server has given these error types.
        if (fetchingStatus === 'LoginRequired') {
            return;
        }
        if (existingData) {
            // make sure we call article page view.
            yield* call(sendCategoryPageEvent, categoryId);
            return;
        }
        const languageId = yield* call(getLanguageId);
        const data = yield* call(fetchCategory, categoryId, languageId);
        if (!data.category) {
            throw new Error('NotFound');
        }
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'category',
            categoryId,
            path,
            data,
        }));
    }
    catch (error) {
        if (!error.response) {
            yield* call(reportError, error);
        }
        const errors = ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'category',
            categoryId,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* requestHomePage(path) {
    var _a;
    // Check to see if we already have the data.
    const existingData = yield* select(getHomePageData);
    const fetchingStatus = yield* select(homePageFetchingStatus);
    // Don't try to reload if server has given these error types.
    if (fetchingStatus === 'LoginRequired') {
        return;
    }
    if (existingData) {
        // make sure we call article page view.
        yield* call(sendHomePageEvent);
        return;
    }
    try {
        const languageId = yield* call(getLanguageId);
        const data = yield* call(fetchHome, languageId);
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'home',
            path,
            data,
        }));
    }
    catch (error) {
        if (!error.response) {
            yield* call(reportError, error);
        }
        const errors = ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'home',
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* requestSearchPage(query, path) {
    var _a;
    try {
        const languageId = yield* call(getLanguageId);
        const data = yield* call(searchArticles, { term: query, languageId });
        yield* put(PageActions.Actions.pageNavComplete({
            pageType: 'search',
            path,
            searchTerm: query,
            data,
        }));
    }
    catch (error) {
        const errors = ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.errors) || [];
        yield* put(PageActions.Actions.pageNavError({
            pageType: 'search',
            searchTerm: query,
            path,
            errorStatus: getErrorStatus(errors),
            errors,
        }));
    }
}
export function* pageNavStart(action) {
    const payload = action.payload;
    switch (payload.pageType) {
        case 'article':
            yield* call(requestArticlePageData, payload.articleId, payload.path);
            return;
        case 'category':
            yield* call(requestCategoryPageData, payload.categoryId, payload.path);
            return;
        case 'home':
            yield* call(requestHomePage, payload.path);
            return;
        case 'search':
            yield* call(requestSearchPage, payload.searchTerm, payload.path);
            return;
        default:
            throwPageType(payload);
    }
}
function throwPageType(p) {
    throw new Error('Unknown page type: ' + p);
}
async function ssoLogout() {
    window.location.href = `/sso/logout${window.location.search}`;
}
export function* checkUserAuth(action) {
    const isUnauth = checkErrorsForUnauth(action.payload.errors || []);
    if (!isUnauth)
        return;
    yield* call(ssoLogout);
}
export function* watchPageNav() {
    yield* takeLatest(PageActions.ActionTypes.PAGE_NAV_START, pageNavStart);
}
export function* watchPageNavErrors() {
    yield takeEvery(PageActions.ActionTypes.PAGE_NAV_ERROR, checkUserAuth);
}
//# sourceMappingURL=page.js.map